import React, { useEffect, useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { getProductCategories, getProducts } from '../apis';
import { appGetLocale, appPathPrefix, isMobileAspectRatio } from '../helpers/appHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { yamazaki_brown, yamazaki_red } from '../styles/color';

const Products = () => {
    const appLanguage = appGetLocale();
    const isEng = appLanguage === 'en';
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [popupData, setPopupData] = useState(null);
    const [currentCategory, setCurrentCategory] = useState({});
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [popupImageHeight, setPopupImageHeight] = useState(0);
    const [finishFirstMenuOpen, setFinishFirstMenuOpen] = useState(false);
    const [menuWidth, setMenuWidth] = useState(0);
    const [menuHeight, setMenuHeight] = useState(0);
    const [isImageClickDisabled, setIsImageClickDisabled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const prefix = appPathPrefix();
    const isMobile = isMobileAspectRatio();
    const containerRef = useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const categorie_id = params.get('categorie_id');
        const product_id = params.get('product_id');

        if (categorie_id && product_id) {
            getData({ categorie_id, product_id, show_image: true });
        }
        else if (categorie_id && currentCategory?.id != categorie_id) {
            getData({ categorie_id });
        }
        else if (categorie_id === null) {
            getData({});
        }

    }, [location.search]);

    useEffect(() => {
        if (containerRef.current) {
            setMenuHeight(containerRef.current.clientHeight);
            setMenuWidth(containerRef.current.clientWidth);
        }
    }, [isMenuOpen]);

    useEffect(() => {
        if (popupData) {
            const imgElement = document.querySelector('.popup-image.show');
            if (imgElement) {
                imgElement.onload = () => {
                    setPopupImageHeight(imgElement.clientHeight);
                };
                setPopupImageHeight(imgElement.clientHeight);
            }
        }
    }, [popupData]);

    const getData = async ({ categorie_id, product_id, show_image = false }) => {
        if (categorie_id) {
            const [product_result, categorie_result] = await Promise.all([getProducts({ category_id: categorie_id }), getProductCategories()]);
            if (!product_result.fail) {
                setProducts(product_result);
            }
            if (!categorie_result.fail) {
                setCategories(categorie_result);

                const item = categorie_result.find(cat => cat.id == categorie_id);
                if (item) setCurrentCategory(item);

                if (show_image) {
                    handleImageClick(product_result.find(item => item.id == product_id));
                } else {
                    navigate(prefix + `/products?categorie_id=${categorie_id}`);
                }
            }
        }
        else {
            const categorie_result = await getProductCategories();
            if (!categorie_result.fail) {
                setCategories(categorie_result);
                setCurrentCategory(categorie_result[0]);

                const product_result = await getProducts({ category_id: categorie_result[0]?.id });

                if (!product_result.fail) {
                    setProducts(product_result);
                }
                navigate(prefix + `/products?categorie_id=${categorie_result[0]?.id}`);
            }
        }
    }

    const handleImageClick = (item, updateUrl = true) => {
        if (isImageClickDisabled) return;
        setIsImageClickDisabled(true);
        setTimeout(() => 
            setIsImageClickDisabled(false)
        , 250);

        setPopupData(item);
        if (updateUrl) {
            navigate(prefix + `/products?categorie_id=${item.catagory_id}&product_id=${item.id}`);
        }
    };

    const closePopup = () => {
        setPopupData(null);
        navigate(prefix + '/products?categorie_id=' + currentCategory?.id);
    };

    const handleCategoryClick = (item) => {
        navigate(prefix + `/products?categorie_id=${item.id}`);
        setIsMenuOpen(false);
    };

    const handleMenuToggle = () => {
        if (isMenuOpen) {
            const menu = document.querySelector('.products-categorie-list-mobile-menu');
            const icon = document.querySelector('.products-categorie-list-mobile-icon');
            menu.classList.add('closing');
            icon.classList.add('rotating');
            
            setTimeout(() => {
                setIsMenuOpen(false);
                menu.classList.remove('rotating');
                menu.classList.remove('closing');
            }, 300);
        } else {
            !finishFirstMenuOpen && setFinishFirstMenuOpen(true);
            setIsMenuOpen(true);
        }
    };

    const formatDescription = (description) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return description.split(urlRegex).map((part, index) => {
            if (urlRegex.test(part)) {
                return <a key={index} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
            }
            return part;
        });
    };

    const NavBar = () => (
        <div className="products-nav">
            <a href={prefix + "/"} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    <FormattedMessage id={'menus.home'} />
                </span>
            </a>
            <span> {">"} </span>
            &nbsp;&nbsp;
            <span>
                <FormattedMessage id={'menus.products'} />
            </span>
            <span> {">"} </span>
            &nbsp;&nbsp;
            <span style={{ fontWeight: 'bold' }}>
                {currentCategory?.name}
            </span>
        </div>
    );

    const MobileCategorieList = () => (
        <div className={"products-categorie-list-mobile-container" + (isMenuOpen ? " open" : "")} onClick={handleMenuToggle} ref={containerRef}>
            <FormattedMessage id="products.mobileCategorie" />
            <div className='products-categorie-list-mobile-icon'>
                <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 17.395 10.126" >
                    <path d="M787.667,177.167s6.834,7.714,7.286,7.714,7.286-7.714,7.286-7.714" transform="translate(-786.255 -175.755)" fill="none" stroke="#ab0a0e" strokeLinecap="round" strokeLinejoin="bevel" strokeWidth="2" />
                </svg>
            </div>
        </div>
    );

    const MobileCategorieMenu = () => (
        <div className="products-categorie-list-mobile-menu" style={{ width: menuWidth, marginTop: `calc(1px + ${menuHeight}px)` }}>
            {categories.map((item, index) => (
                <div key={index} className="products-categorie-list-mobile-menu-item" onClick={() => handleCategoryClick(item)}>
                    <img src={item.web_icon_url} />
                    <text style={{ fontSize: isEng ? '0.8em' : '1em' }}>{item.name}</text>
                    <svg xmlns="http://www.w3.org/2000/svg" height="100%" viewBox="0 0 8.049 13.471" style={{ marginLeft: 'auto', maxHeight: '2vh' }}>
                        <path d="M0,0S4.994,5.637,5.324,5.637,10.648,0,10.648,0" transform="translate(1.412 12.06) rotate(-90)" fill="none" stroke="#fff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                </div>
            ))}
            <div className="products-categorie-list-mobile-menu-close" onClick={handleMenuToggle}>
                <svg xmlns="http://www.w3.org/2000/svg" height="100%" viewBox="0 0 10.121 10.121" style={{ marginRight: '2vw', maxHeight: '2vw' }}>
                    <g transform="translate(1.061 1.061)">
                        <line x2="8" y2="8" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="1.5" />
                        <line x1="8" y2="8" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="1.5" />
                    </g>
                </svg>
                <FormattedMessage id="products.closeMenu" />
            </div>
        </div>
    )

    const CategorieList = () => {
        const icon_width = (100 - 40) / categories.length;
        const icon_height = icon_width * 143 / 140;

        return (
            <div className='products-categorie-list'>
                {categories && categories?.map((item, index) => (
                    <div className='products-categorie-item' style={{ width: icon_width + 'vw', height: icon_height + 'vw', backgroundColor: currentCategory?.id == item.id ? '#EB282D' : yamazaki_red }} key={index} onClick={() => handleCategoryClick(item)}>
                        <img src={item.web_icon_url} />
                        <text style={{ fontSize: isEng ? '1.1vw' : '1.5vw', bottom: isEng?'10%':'5%' }}>{item.name}</text>
                    </div>
                ))}
            </div>
        );
    };

    const ProductsList = () => {
        const emptySlots = isMobile?(3 - (products.length % 3)):(5 - (products.length % 5));

        const ProductsItem = () => {
            return (
                products?.map((item, index) => (
                    <div className="products-item" key={index} onClick={() => handleImageClick(item)}>
                        <img src={item.image_url} />
                        <text style={{ fontSize: isMobile ? '12px':'1.5vw', marginTop: isMobile?'2px':'none' }}>{item.name}</text>
                    </div>
                ))
            );
        };

        return (
            <div className='products-container'>
                {products && <ProductsItem />}
                {Array.from({ length: emptySlots }).map((_, index) => (
                    <div className='products-item-empty' key={index} />
                ))}
            </div>
        );
    }

    return (
        <div className='products'>
            <div className="products-background">
                <img src='./images/top_background.png' />
                <text className="products-title" style={{ letterSpacing: isEng?'0':'1.5vw' }}>
                    <FormattedMessage id={'products.title'} />
                </text>
            </div>
            <NavBar />
            {!isMobile && <CategorieList />}
            {isMobile &&
                <div className='products-categorie-list mobile'>
                    <MobileCategorieList />
                    {isMenuOpen && <MobileCategorieMenu />}
                </div>}
            <ProductsList />
            {popupData && (
                <>
                    <div className="popup-overlay show" onClick={closePopup} />
                    <div className='popup-container'>
                        <img src={popupData.web_large_image_url} className="popup-image show product" onClick={closePopup} />
                        <div className="popup-text show" style={{ top: `calc(20% + ${popupImageHeight}px)` }}>
                            <text style={{ fontWeight: 'bold' }}>
                                {popupData.title}
                            </text>
                            <text>{formatDescription(popupData.descript)}</text>
                        </div>
                        {isMobile &&
                            <div className='popup-svg-close-btn' onClick={closePopup}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25">
                                    <g transform="translate(-277 -94)">
                                        <g transform="translate(277 94)" fill="none" stroke="#fff" strokeWidth="2">
                                            <circle cx="12.5" cy="12.5" r="12.5" stroke="none" />
                                            <circle cx="12.5" cy="12.5" r="11.5" fill="none" />
                                        </g>
                                        <g transform="translate(285.444 102.444)">
                                            <g>
                                                <path d="M0,0,7.975,7.975" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="3.5" />
                                                <path d="M7.975,0,0,7.975" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="3.5" />
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                            </div>}
                    </div>
                </>
            )}
        </div>
    )
};

export default Products;